import {
  AlertCircle,
  CheckCircle,
  Loader2,
  Calculator,
  FileText,
  Info,
  Paperclip,
} from 'lucide-react'
import { useState } from 'react'
import { formatAmount } from '../../utils/amountFormatter'
import type {
  CT600FinancialDataForm,
  CT600BalanceSheetForm,
  CT600TaxCompForm,
  CT600CompanyInfoForm,
  CT600SupplementaryState,
} from '../../types/ct600Filing'
import type {
  Ct600CalculationBreakdown,
  Ct600PeriodComputation,
  GenericRecord,
} from '../../types'

function fmtDate(iso: string) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return iso
  }
}

function PeriodBreakdownCard({
  period,
  isLpoa,
}: {
  period: Ct600PeriodComputation
  isLpoa: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const headerColor =
    period.index === 1
      ? 'bg-blue-50 border-blue-200 text-blue-900'
      : 'bg-amber-50 border-amber-200 text-amber-900'

  return (
    <div className='rounded-xl border border-gray-200 bg-white overflow-hidden'>
      <div
        className={`flex items-center justify-between border-b px-5 py-3 ${headerColor}`}
      >
        <div className='flex items-center gap-2'>
          <span
            className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${
              period.index === 1
                ? 'bg-blue-100 text-blue-700'
                : 'bg-amber-100 text-amber-800'
            }`}
          >
            {period.index}
          </span>
          <h4 className='text-sm font-semibold'>{period.label}</h4>
        </div>
        {isLpoa && (
          <span
            className={`inline-flex items-center gap-1 rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-medium border ${
              period.accountsAttached
                ? 'text-green-700 border-green-200'
                : 'text-gray-700 border-gray-200'
            }`}
          >
            <Paperclip className='h-2.5 w-2.5' />
            {period.accountsAttached
              ? 'iXBRL accounts attached'
              : 'No accounts (filed with separate return)'}
          </span>
        )}
      </div>

      <div className='px-5 py-4 space-y-3'>
        <div className='flex items-center justify-between text-xs text-gray-500'>
          <span>
            {fmtDate(period.start)} — {fmtDate(period.end)}
          </span>
          <span>{period.days} days</span>
        </div>

        {/* Line items */}
        <div className='rounded-lg bg-gray-50 px-3 py-2'>
          <div className='space-y-1'>
            {period.lineItems.map((li, i) => (
              <div
                key={i}
                className='flex items-center justify-between text-xs'
              >
                <span className='text-gray-600'>
                  {li.boxNumber && (
                    <span className='inline-block mr-2 rounded bg-white px-1.5 py-0.5 font-mono text-[10px] text-gray-500 border border-gray-200'>
                      {li.boxNumber}
                    </span>
                  )}
                  {li.label}
                </span>
                <span className='font-medium text-gray-900'>
                  £{formatAmount(li.amount)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Tax */}
        <div className='grid grid-cols-3 gap-2'>
          <div className='rounded-lg border border-gray-200 px-3 py-2'>
            <p className='text-[10px] uppercase tracking-wide text-gray-500'>
              Chargeable Profits
            </p>
            <p className='mt-0.5 text-sm font-bold text-gray-900'>
              £{formatAmount(period.chargeableProfits)}
            </p>
          </div>
          <div className='rounded-lg border border-gray-200 px-3 py-2'>
            <p className='text-[10px] uppercase tracking-wide text-gray-500'>
              Marginal Relief
            </p>
            <p className='mt-0.5 text-sm font-bold text-gray-900'>
              £{formatAmount(period.tax.marginalRelief)}
            </p>
          </div>
          <div className='rounded-lg border border-green-200 bg-green-50 px-3 py-2'>
            <p className='text-[10px] uppercase tracking-wide text-green-700'>
              Net Tax
            </p>
            <p className='mt-0.5 text-sm font-bold text-green-900'>
              £{formatAmount(period.tax.net)}
            </p>
          </div>
        </div>

        {period.thresholds?.rationale && (
          <p className='text-[11px] text-gray-500 italic'>
            {period.thresholds.rationale}
          </p>
        )}

        {/* FY breakdown */}
        {Array.isArray(period.fyBreakdown) && period.fyBreakdown.length > 0 && (
          <div>
            <button
              type='button'
              onClick={() => setExpanded(v => !v)}
              className='text-xs font-medium text-gray-700 hover:text-gray-900 underline-offset-2 hover:underline'
            >
              {expanded ? 'Hide' : 'Show'} financial-year split (
              {period.fyBreakdown.length})
            </button>
            {expanded && (
              <div className='mt-2 overflow-x-auto'>
                <table className='w-full text-xs'>
                  <thead>
                    <tr className='text-left text-gray-500'>
                      <th className='py-1 pr-3 font-medium'>FY</th>
                      <th className='py-1 pr-3 font-medium'>Period</th>
                      <th className='py-1 pr-3 font-medium'>Days</th>
                      <th className='py-1 pr-3 font-medium'>Profit</th>
                      <th className='py-1 pr-3 font-medium'>Rate</th>
                      <th className='py-1 pr-3 font-medium'>Gross Tax</th>
                      <th className='py-1 pr-3 font-medium'>Marginal Relief</th>
                      <th className='py-1 font-medium'>Net Tax</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-100'>
                    {period.fyBreakdown.map((fy, i) => (
                      <tr key={i} className='text-gray-700'>
                        <td className='py-1 pr-3 font-mono'>
                          FY{String(fy.fy).slice(-2)}
                        </td>
                        <td className='py-1 pr-3'>
                          {fmtDate(fy.fyStart)} — {fmtDate(fy.fyEnd)}
                        </td>
                        <td className='py-1 pr-3'>{fy.days}</td>
                        <td className='py-1 pr-3'>
                          £{formatAmount(fy.profit)}
                        </td>
                        <td className='py-1 pr-3'>
                          {fy.band === 'small'
                            ? `${fy.smallProfitsRate}%`
                            : `${fy.mainRate}%`}{' '}
                          <span className='text-gray-400'>({fy.band})</span>
                        </td>
                        <td className='py-1 pr-3'>
                          £{formatAmount(fy.grossTax)}
                        </td>
                        <td className='py-1 pr-3'>
                          £{formatAmount(fy.marginalRelief)}
                        </td>
                        <td className='py-1 font-medium'>
                          £{formatAmount(fy.netTax)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function SummaryRow({
  label,
  value,
  bold = false,
}: {
  label: string
  value: string
  bold?: boolean
}) {
  return (
    <div className='flex items-center justify-between py-1.5'>
      <span
        className={`text-sm ${bold ? 'font-semibold text-gray-900' : 'text-gray-600'}`}
      >
        {label}
      </span>
      <span
        className={`text-sm ${bold ? 'font-bold text-gray-900' : 'font-medium text-gray-900'}`}
      >
        {value}
      </span>
    </div>
  )
}

function SummaryCard({
  title,
  icon,
  children,
}: {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className='rounded-lg border border-gray-200 bg-white'>
      <div className='flex items-center gap-2 border-b border-gray-100 px-5 py-3'>
        {icon}
        <h3 className='text-sm font-semibold text-gray-900'>{title}</h3>
      </div>
      <div className='px-5 py-4'>{children}</div>
    </div>
  )
}

interface Props {
  companyInfo: CT600CompanyInfoForm
  financialData: CT600FinancialDataForm
  balanceSheet: CT600BalanceSheetForm
  taxComp: CT600TaxCompForm
  supplementary: CT600SupplementaryState
  calculationResult: GenericRecord | null
  calculationBreakdown: Ct600CalculationBreakdown | null
  ct600Boxes: GenericRecord | null
  formStatus: string | null
  isCalculating: boolean
  onCalculate: () => void
}

export default function CT600StepReview({
  companyInfo,
  financialData,
  balanceSheet,
  taxComp,
  supplementary,
  calculationResult,
  calculationBreakdown,
  formStatus,
  isCalculating,
  onCalculate,
}: Props) {
  const p = (v: string) => parseFloat(v) || 0

  // P&L calculations
  const totalIncome = p(financialData.turnover) + p(financialData.otherIncome)
  const grossProfit = totalIncome - p(financialData.costOfSales)
  const operatingProfit =
    grossProfit -
    p(financialData.distributionCosts) -
    p(financialData.administrativeExpenses)
  const profitBeforeTax =
    operatingProfit +
    p(financialData.interestReceivable) -
    p(financialData.interestPayable)
  const profitAfterTax = profitBeforeTax - p(financialData.taxExpense)

  // Tax computation
  const addBacks =
    p(taxComp.depreciation) + p(taxComp.entertainmentDisallowable)
  const deductions =
    p(taxComp.capitalAllowances) +
    p(taxComp.bankInterest) +
    p(taxComp.charitableDonations)
  const adjustedProfit = profitBeforeTax + addBacks - deductions

  // Balance sheet totals
  const totalFixedAssets =
    p(balanceSheet.intangibleAssets) + p(balanceSheet.tangibleAssets)
  const totalCurrentAssets =
    p(balanceSheet.stocks) +
    p(balanceSheet.debtors) +
    p(balanceSheet.cashAtBankAndInHand)
  const shareholdersFunds =
    p(balanceSheet.calledUpShareCapital) + p(balanceSheet.profitAndLossAccount)

  // Calculation result values
  const corporationTax = calculationResult?.corporationTax as number | undefined
  const taxRate = calculationResult?.effectiveRate as number | undefined
  const marginalRelief = calculationResult?.marginalRelief as number | undefined

  return (
    <div className='space-y-6'>
      <div>
        <h2 className='text-xl font-bold text-gray-900'>Tax Computation</h2>
        <p className='mt-1 text-sm text-gray-600'>
          Review your entries and calculate the corporation tax liability.
        </p>
      </div>

      {/* Calculate Button */}
      <div className='space-y-2'>
        {!calculationResult && !calculationBreakdown && (
          <p className='text-sm font-medium text-[#F04438]'>
            Please click the button below to calculate your tax before you can
            proceed.
          </p>
        )}
        <div className='flex items-center gap-4'>
        <button
          type='button'
          onClick={onCalculate}
          disabled={isCalculating}
          className='flex items-center gap-2 rounded-lg bg-gray-900 px-6 py-3 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50 transition-colors'
        >
          {isCalculating ? (
            <Loader2 className='h-4 w-4 animate-spin' />
          ) : (
            <Calculator className='h-4 w-4' />
          )}
          {isCalculating ? 'Calculating...' : 'Calculate Tax'}
        </button>
        {formStatus && (
          <span
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              formStatus === 'completed'
                ? 'bg-green-100 text-green-700'
                : 'bg-yellow-100 text-yellow-700'
            }`}
          >
            {formStatus.charAt(0).toUpperCase() + formStatus.slice(1)}
          </span>
        )}
        </div>
      </div>

      {/* Calculation Result (per-period breakdown when available) */}
      {calculationBreakdown ? (
        <div className='space-y-4'>
          <div className='rounded-xl border-2 border-green-200 bg-green-50 p-5'>
            <div className='flex items-start gap-3'>
              <CheckCircle className='h-5 w-5 text-green-600 mt-0.5' />
              <div className='flex-1 min-w-0'>
                <div className='flex flex-wrap items-baseline gap-3'>
                  <h3 className='text-base font-semibold text-green-900'>
                    {calculationBreakdown.isLpoa
                      ? 'LPOA tax computed across two periods'
                      : 'Calculation complete'}
                  </h3>
                  <span className='text-xs text-green-700'>
                    {calculationBreakdown.periodOfAccount.days} day period of
                    account
                  </span>
                </div>
                <p className='mt-1 text-2xl font-bold text-green-900'>
                  Total Corporation Tax: £
                  {formatAmount(calculationBreakdown.totalCorporationTax)}
                </p>
              </div>
            </div>
            {calculationBreakdown.warnings.length > 0 && (
              <div className='mt-4 space-y-2'>
                {calculationBreakdown.warnings.map((w, i) => (
                  <div
                    key={i}
                    className='flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2'
                  >
                    <Info className='h-4 w-4 mt-0.5 text-amber-600 flex-shrink-0' />
                    <p className='text-xs text-amber-800'>{w}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className='grid grid-cols-1 gap-4'>
            {calculationBreakdown.periods.map(p => (
              <PeriodBreakdownCard
                key={p.index}
                period={p}
                isLpoa={calculationBreakdown.isLpoa}
              />
            ))}
          </div>
        </div>
      ) : calculationResult ? (
        <div className='rounded-xl border-2 border-green-200 bg-green-50 p-6'>
          <div className='flex items-center gap-2 mb-4'>
            <CheckCircle className='h-5 w-5 text-green-600' />
            <h3 className='text-base font-semibold text-green-900'>
              Calculation Complete
            </h3>
          </div>
          <div className='grid grid-cols-1 sm:grid-cols-3 gap-6'>
            <div className='text-center'>
              <div className='text-xs font-medium text-green-700 uppercase tracking-wide'>
                Taxable Profit
              </div>
              <div className='mt-1 text-2xl font-bold text-green-900'>
                £{formatAmount(adjustedProfit)}
              </div>
            </div>
            <div className='text-center'>
              <div className='text-xs font-medium text-green-700 uppercase tracking-wide'>
                Corporation Tax
              </div>
              <div className='mt-1 text-2xl font-bold text-green-900'>
                £
                {corporationTax !== undefined
                  ? formatAmount(corporationTax)
                  : '—'}
              </div>
            </div>
            <div className='text-center'>
              <div className='text-xs font-medium text-green-700 uppercase tracking-wide'>
                Effective Rate
              </div>
              <div className='mt-1 text-2xl font-bold text-green-900'>
                {taxRate !== undefined ? `${taxRate}%` : '—'}
              </div>
            </div>
          </div>
          {marginalRelief !== undefined && marginalRelief > 0 && (
            <div className='mt-4 rounded-lg bg-green-100 p-3 text-center'>
              <span className='text-sm text-green-800'>
                Marginal Relief Applied:{' '}
                <strong>£{formatAmount(marginalRelief)}</strong>
              </span>
            </div>
          )}
        </div>
      ) : null}

      {!calculationResult && !isCalculating && (
        <div className='flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4'>
          <AlertCircle className='h-5 w-5 flex-shrink-0 text-amber-600' />
          <p className='text-sm text-amber-800'>
            Click "Calculate Tax" to compute the corporation tax liability
            before proceeding to submission.
          </p>
        </div>
      )}

      {/* Summary Cards */}
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
        <SummaryCard
          title='Company Information'
          icon={<FileText className='h-4 w-4 text-gray-500' />}
        >
          <div className='space-y-1'>
            <SummaryRow
              label='Company'
              value={companyInfo.details.companyName || '—'}
            />
            <SummaryRow
              label='CRN'
              value={companyInfo.details.companyRegistrationNumber || '—'}
            />
            <SummaryRow label='UTR' value={companyInfo.details.utr || '—'} />
            <SummaryRow
              label='Contact'
              value={companyInfo.contact.name || '—'}
            />
          </div>
        </SummaryCard>

        <SummaryCard
          title='Profit & Loss'
          icon={<FileText className='h-4 w-4 text-gray-500' />}
        >
          <div className='space-y-1'>
            <SummaryRow
              label='Turnover'
              value={`£${formatAmount(financialData.turnover)}`}
            />
            <SummaryRow
              label='Total Income'
              value={`£${formatAmount(totalIncome)}`}
            />
            <SummaryRow
              label='Gross Profit'
              value={`£${formatAmount(grossProfit)}`}
            />
            <SummaryRow
              label='Operating Profit'
              value={`£${formatAmount(operatingProfit)}`}
            />
            <SummaryRow
              label={profitBeforeTax < 0 ? 'Loss Before Tax' : 'Profit Before Tax'}
              value={`£${formatAmount(Math.abs(profitBeforeTax))}`}
            />
            <SummaryRow
              label='Tax Expense'
              value={`£${formatAmount(financialData.taxExpense)}`}
            />
            <SummaryRow
              label={profitAfterTax < 0 ? 'Loss After Tax' : 'Profit After Tax'}
              value={`£${formatAmount(Math.abs(profitAfterTax))}`}
              bold
            />
          </div>
        </SummaryCard>

        <SummaryCard
          title='Balance Sheet'
          icon={<FileText className='h-4 w-4 text-gray-500' />}
        >
          <div className='space-y-1'>
            <SummaryRow
              label='Fixed Assets'
              value={`£${formatAmount(totalFixedAssets)}`}
            />
            <SummaryRow
              label='Current Assets'
              value={`£${formatAmount(totalCurrentAssets)}`}
            />
            <SummaryRow
              label="Shareholders' Funds"
              value={`£${formatAmount(shareholdersFunds)}`}
              bold
            />
          </div>
        </SummaryCard>

        <SummaryCard
          title='Tax Adjustments'
          icon={<FileText className='h-4 w-4 text-gray-500' />}
        >
          <div className='space-y-1'>
            <SummaryRow
              label='Profit Before Tax'
              value={`£${formatAmount(profitBeforeTax)}`}
            />
            <SummaryRow
              label='Add-backs'
              value={`£${formatAmount(addBacks)}`}
            />
            <SummaryRow
              label='Deductions'
              value={`(£${formatAmount(deductions)})`}
            />
            <SummaryRow
              label='Adjusted Profit'
              value={`£${formatAmount(adjustedProfit)}`}
              bold
            />
          </div>
        </SummaryCard>
      </div>

      {/* Supplementary Pages Summary */}
      {(supplementary.requirements.ct600a ||
        supplementary.requirements.ct600c ||
        supplementary.requirements.ct600l) && (
        <SummaryCard
          title='Supplementary Pages'
          icon={<FileText className='h-4 w-4 text-gray-500' />}
        >
          <div className='space-y-2'>
            {supplementary.requirements.ct600a && (
              <div className='flex items-center gap-2'>
                <CheckCircle className='h-4 w-4 text-green-600' />
                <span className='text-sm text-gray-700'>
                  CT600A — {supplementary.ct600a.outstandingLoans.length}{' '}
                  loan(s), Tax: £
                  {formatAmount(supplementary.ct600a.taxChargeable)}
                </span>
              </div>
            )}
            {supplementary.requirements.ct600c && (
              <div className='flex items-center gap-2'>
                <CheckCircle className='h-4 w-4 text-green-600' />
                <span className='text-sm text-gray-700'>
                  CT600C — Group Relief: £
                  {formatAmount(supplementary.ct600c.reliefAmount)}
                </span>
              </div>
            )}
            {supplementary.requirements.ct600l && (
              <div className='flex items-center gap-2'>
                <CheckCircle className='h-4 w-4 text-green-600' />
                <span className='text-sm text-gray-700'>
                  CT600L — R&D Credit: £
                  {formatAmount(supplementary.ct600l.rdCredit)}
                </span>
              </div>
            )}
          </div>
        </SummaryCard>
      )}
    </div>
  )
}
